import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { componentState } from "../../atoms/componentState";
import TermosApi, { MeuTermo } from "../../services/termosApi";
import LeitorTermo from "./components/leitorTermo";

/**
 * "Meus termos": o que o estabelecimento já aceitou ou recusou, com o documento
 * exatamente como foi lido na época — o hash gravado prova esse conteúdo.
 */
const STATUS_ROTULO: Record<string, string> = {
  PENDENTE: "Aguardando aceite",
  ACEITO: "Aceito",
  RECUSADO: "Recusado",
  EXPIRADO: "Prazo vencido",
  SUPERADO: "Substituído por versão mais nova",
  CANCELADO: "Cancelado",
};

const STATUS_COR: Record<string, string> = {
  PENDENTE: "blue",
  ACEITO: "green",
  RECUSADO: "red",
  EXPIRADO: "orange",
  SUPERADO: "gray",
  CANCELADO: "gray",
};

export default function MeusTermos() {
  const setHeaderLocation = useSetRecoilState(componentState);

  const [termos, setTermos] = useState<MeuTermo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [documento, setDocumento] = useState<{
    titulo: string;
    html: string;
  } | null>(null);

  useEffect(() => {
    setHeaderLocation("Meus Termos");
    carregar();
    // eslint-disable-next-line
  }, []);

  async function carregar() {
    try {
      setCarregando(true);
      setTermos(await TermosApi.getMeusTermos());
    } catch (error) {
      // lista vazia é o comportamento aceitável aqui
    } finally {
      setCarregando(false);
    }
  }

  async function abrirDocumento(termo: MeuTermo) {
    try {
      const dados = await TermosApi.getDocumento(termo.idTermVersion);
      setDocumento({ titulo: termo.titulo, html: dados.html });
    } catch (error) {
      // documento indisponível: nada a exibir
    }
  }

  return (
    <Flex direction="column" w="100%" h="100%" p="4" overflowY="auto">
      <Text fontSize="xl" fontWeight="bold" mb="1">
        Meus termos
      </Text>
      <Text fontSize="sm" color="gray.600" mb="4">
        Todos os termos apresentados ao seu estabelecimento — aceitos, recusados e os que
        ainda aguardam decisão. O documento fica disponível para leitura a qualquer momento.
      </Text>

      {carregando ? (
        <Flex justifyContent="center" py="10">
          <Spinner />
        </Flex>
      ) : termos.length === 0 ? (
        <Box bgColor="#FFF" borderRadius="6px" p="6">
          <Text color="gray.600">Nenhum termo registrado até o momento.</Text>
        </Box>
      ) : (
        <Box bgColor="#FFF" borderRadius="6px" p="2" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Termo</Th>
                <Th>Versão</Th>
                <Th>Situação</Th>
                <Th>Data</Th>
                <Th>Usuário</Th>
                <Th>Documento</Th>
              </Tr>
            </Thead>
            <Tbody>
              {termos.map((t) => (
                <Tr key={t.idTermNotice}>
                  <Td>
                    {t.titulo}
                    {t.resumo && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {t.resumo}
                      </Text>
                    )}
                  </Td>
                  <Td>{t.versao}</Td>
                  <Td>
                    <Badge colorScheme={STATUS_COR[t.status] ?? "gray"}>
                      {STATUS_ROTULO[t.status] ?? t.status}
                    </Badge>
                    {t.motivoRecusa && (
                      <Text fontSize="xs" color="gray.600">
                        {t.motivoRecusa}
                      </Text>
                    )}
                    {t.status === "PENDENTE" && (
                      <Text fontSize="xs" color={t.vencido ? "red.500" : "gray.500"}>
                        {t.vencido
                          ? `prazo vencido em ${moment(t.prazoAte).format("DD/MM/YY")}`
                          : `aceitar até ${moment(t.prazoAte).format("DD/MM/YY")}`}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    {t.decididoEm
                      ? moment(t.decididoEm).format("DD/MM/YYYY HH:mm")
                      : moment(t.publicadoEm).format("DD/MM/YYYY HH:mm")}
                    <Text fontSize="xs" color="gray.500">
                      {t.decididoEm ? "decidido" : "publicado"}
                    </Text>
                  </Td>
                  <Td>{t.decididoPor || "-"}</Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => abrirDocumento(t)}
                    >
                      Ver
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal
        isOpen={!!documento}
        onClose={() => setDocumento(null)}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{documento?.titulo}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="5">
            {documento && (
              <LeitorTermo
                html={documento.html}
                onFimDaLeitura={() => {}}
                altura="65vh"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
